import { createSelector, lruMemoize } from 'reselect';

import type {
  AssetsAssetSchemaTypedWithId,
  AssetsCollectionSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';
import type {
  FilesFileSchemaWithId,
  FilesFormatSchemaWithId,
} from '@iconik-io/iconik-api/src/services/files';
import type { SearchSavedSearchResultsSchemaWithId } from '@iconik-io/iconik-api/src/services/search';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import {
  COLLECTION_PAGE_ROUTE,
  PORTAL_EDIT_MEDIA_PAGE_ROUTE,
  PORTAL_EDIT_PAGE_ROUTE,
  PORTAL_MEDIA_PAGE_ROUTE,
  PORTAL_PAGE_ROUTE,
  SAVED_SEARCH_PAGE_ROUTE,
} from '../../constants/routes';
import type { SearchScope } from '../../router/search_scope';
import type { AppState } from '../../store/types';
import type { SearchItemKeyframes } from '../../types/search_item_keyframes';
import { getPortalDisplayTitle } from '../../utils/get_portal_display_title';
import { DEFAULT_SEARCH_FIELDS } from '../pages/search/constants';

const getRouteParams = (state: AppState) => state.routeParams;
const getSearchQuery = (state: AppState) =>
  state.panelsState.searchFilters.query;
const getCollections = (state: AppState) => state.entities.collections;
const getSavedSearches = (state: AppState) =>
  state.entities.saved_search_results;

const PORTAL_DETAIL_ROUTES: string[] = [
  PORTAL_PAGE_ROUTE,
  PORTAL_MEDIA_PAGE_ROUTE,
  PORTAL_EDIT_PAGE_ROUTE,
  PORTAL_EDIT_MEDIA_PAGE_ROUTE,
];

// Narrowed to the one portal the route shows, so the selector does not
// recompute (and re-render the topbar) on every unrelated entity write.
const getRoutePortal = (state: AppState) =>
  PORTAL_DETAIL_ROUTES.includes(state.routeParams.route)
    ? state.entities.portals[state.routeParams.params.id]
    : undefined;

const getRoutePortalPlaylist = (state: AppState) => {
  const playlistId = getRoutePortal(state)?.playlist_id;
  return playlistId ? state.entities.playlists[playlistId] : undefined;
};

export type SearchContextEntity =
  | SearchSavedSearchResultsSchemaWithId
  | AssetsCollectionSchemaWithId;

/**
 * A portal the route is showing. Not a `SearchContextEntity`: it narrows a
 * client-side list rather than a /search query, and its title has to be
 * resolved through its playlist.
 */
export type SearchContextPortal = {
  id: string;
  title: string;
};

export const selectSearchContext = createSelector(
  getRouteParams,
  getSearchQuery,
  getCollections,
  getSavedSearches,
  getRoutePortal,
  getRoutePortalPlaylist,
  (
    routeParams,
    searchQuery,
    collections,
    savedSearches,
    routePortal,
    routePortalPlaylist,
  ): {
    contextEntity: SearchContextEntity | undefined;
    contextPortal: SearchContextPortal | undefined;
    scope: SearchScope | undefined;
    query: string | undefined;
  } => {
    let contextEntity: SearchContextEntity | undefined;
    const scope = routeParams.searchScope;
    // A scope keeps its term in the URL. `searchFilters.query` is the
    // asset-search query, and a stale value there would otherwise shadow it.
    const query = scope
      ? routeParams.query.search
      : searchQuery || routeParams.query.search;

    if (routeParams.route === COLLECTION_PAGE_ROUTE) {
      contextEntity = collections[routeParams.params.id];
    }
    if (routeParams.route === SAVED_SEARCH_PAGE_ROUTE) {
      contextEntity = savedSearches[routeParams.params.id];
    }

    // Resolved here rather than in label code so the playlist-name fallback
    // lives in one place.
    const contextPortal = routePortal
      ? {
          id: routePortal.id,
          title: getPortalDisplayTitle(routePortal, routePortalPlaylist),
        }
      : undefined;

    return { contextEntity, contextPortal, scope, query };
  },
);

export const combineSearchItemFromAsset = lruMemoize(
  (
    asset: AssetsAssetSchemaTypedWithId | SearchItemEntity | undefined,
    formats: FilesFormatSchemaWithId[],
    files: FilesFileSchemaWithId[],
  ) => {
    if (!asset) {
      return undefined;
    }
    return {
      ...asset,
      object_type: OBJECT_TYPE_ASSETS,
      formats,
      files,
    };
  },
);

const getSearchFilters = (state: AppState) => state.panelsState.searchFilters;

const excludedFiltersInCount = new Set(['status', 'in_collections']);

export const selectIsRootOnlyBrowse = createSelector(
  getSearchFilters,
  (searchFilters) => {
    if (searchFilters.query) {
      return false;
    }

    const disabledFilters = new Set(searchFilters.disabledFilters ?? []);

    const hasActiveFacets = Object.entries(searchFilters.facetValues).some(
      ([name, values]) =>
        !disabledFilters.has(name) &&
        Array.isArray(values) &&
        values.length > 0,
    );
    if (hasActiveFacets) {
      return false;
    }

    const hasActiveFilters = Object.entries(searchFilters.filterValues).some(
      ([name, value]) => {
        if (name === 'status' || disabledFilters.has(name)) {
          return false;
        }
        if (Array.isArray(value) && value.length > 0) {
          return true;
        }
        if (value && !Array.isArray(value)) {
          return true;
        }
        return false;
      },
    );

    return !hasActiveFilters;
  },
);

export const selectFiltersCount = createSelector(
  getSearchFilters,
  (searchFilters) => {
    const disabledFilters = new Set(searchFilters.disabledFilters ?? []);

    const filtersLength = Object.keys(searchFilters.filterValues).filter(
      (name) => {
        const isFilterEmpty = Array.isArray(searchFilters.facetValues[name])
          ? searchFilters.facetValues[name].length === 0
          : false;
        return (
          !excludedFiltersInCount.has(name) &&
          !isFilterEmpty &&
          !disabledFilters.has(name)
        );
      },
    ).length;

    const facetsLength = Object.keys(searchFilters.facetValues).filter(
      (name) => {
        const isFilterEmpty = Array.isArray(searchFilters.facetValues[name])
          ? searchFilters.facetValues[name].length === 0
          : false;
        return !isFilterEmpty && !disabledFilters.has(name);
      },
    ).length;
    return filtersLength + facetsLength;
  },
);

const getKeyframes = (state: AppState) => state.entities.keyframes;

const getKeyframesIds = (state: AppState) =>
  state.entityKeyframes.keyframesIdsByEntityId;

const getKeyframesLoading = (state: AppState) =>
  state.entityKeyframes.loadingByEntityId;

export const selectKeyframesUpdate = createSelector(
  [getKeyframes, getKeyframesIds, getKeyframesLoading],
  (keyframes, ids, loading) => {
    const result: SearchItemKeyframes = {};

    Object.keys(loading).forEach((key) => {
      result[key] = {
        loading: Boolean(loading[key]),
        objects: [],
      };
    });
    Object.keys(ids).forEach((id) => {
      const objects = Object.values(keyframes).filter(
        (keyframe) =>
          ids[id].includes(keyframe.id) && keyframe.type !== 'KEYFRAME_MAP',
      );
      result[id] = {
        objects,
        loading: Boolean(loading[id]),
      };
    });
    return result;
  },
);

const getSearchInTranscriptions = (state: AppState) =>
  state.settings.merged.search_in_transcriptions;

const getSavedSearchCriteria = (state: AppState) =>
  state.entities.saved_search_results[state.routeParams.params.id]
    ?.search_criteria_document;

const getIsSavedSearchRoute = (state: AppState) =>
  state.routeParams.route === SAVED_SEARCH_PAGE_ROUTE;

export const selectSearchFields = createSelector(
  getSearchInTranscriptions,
  getIsSavedSearchRoute,
  getSavedSearchCriteria,
  (searchInTranscriptions, isSavedSearchRoute, savedSearchCriteria) => {
    const savedSearchCriteriaSearchFields =
      savedSearchCriteria?.criteria?.search_fields;
    if (isSavedSearchRoute && savedSearchCriteriaSearchFields) {
      return savedSearchCriteriaSearchFields;
    }
    if (searchInTranscriptions) {
      return DEFAULT_SEARCH_FIELDS.concat('transcription_text');
    }
    return DEFAULT_SEARCH_FIELDS;
  },
);
